import logger from "@cayo/shared/libs/logger";
import { authContextStore, AuthContextStore } from "@cayo/shared/settings/authContextStore";
import tokenStorage, { ITokenStorage } from "@cayo/shared/settings/tokenStorage";
import type { OAuthClient } from "@cayo/shared/types/manifest";
import { NewAccessTokenRequestService } from "./new-access-token-request.service";
import { getSSO, isValidAuthMode } from "../api/sso";
import type { SSO } from "../api/sso";

export type AuthRecoveryNextAction = "retry-request" | "redirect-to-login";

export interface AuthRecoveryResult {
  action: AuthRecoveryNextAction;
}

export interface RecoverAuthSessionOptions {
  response: Response;
  tokenEndpoint: string;
  originalFetch: typeof fetch;
  oauthClient?: OAuthClient;
  authorityUri?: string;
}

type GetSso = () => SSO | undefined;

export class AuthRecoveryService {
  private readonly log = logger.getLogger("AuthRecoveryService");

  constructor(
    private readonly authTokens: ITokenStorage,
    private readonly authContext: AuthContextStore,
    private readonly getSso: GetSso
  ) {}

  public async recoverAuthSession(options: RecoverAuthSessionOptions): Promise<AuthRecoveryResult> {
    const { response } = options;

    if (this._shouldTrySsoRecovery(response)) {
      return this._recoverSsoSession(options);
    }

    return this._recoverLocalAuthSession(options);
  }

  private async _recoverSsoSession(
    options: RecoverAuthSessionOptions
  ): Promise<AuthRecoveryResult> {
    const { authorityUri } = options;
    const sso = this.getSso();

    this.log.debug("recoverAuthSession -> SSO recovery");

    if (!sso) {
      this._clearAuthSession();

      return { action: "redirect-to-login" };
    }

    const silentResult = await sso.refreshTokenSilent({ authorityUri });
    if (silentResult.type === "success") {
      sso.persistAuthenticationResult(silentResult.response);

      return { action: "retry-request" };
    }

    this.log.debug("recoverAuthSession -> SSO recovery failed", silentResult.error);
    this._clearAuthSession();

    return { action: "redirect-to-login" };
  }

  private async _recoverLocalAuthSession(
    options: RecoverAuthSessionOptions
  ): Promise<AuthRecoveryResult> {
    const { tokenEndpoint, originalFetch, oauthClient } = options;

    if (!this.authTokens.hasRefreshToken()) {
      this.log.debug("recoverAuthSession -> no refresh token");
      this._clearAuthSession();

      return { action: "redirect-to-login" };
    }

    const refreshService = new NewAccessTokenRequestService(
      tokenEndpoint,
      this.authTokens,
      originalFetch,
      oauthClient
    );

    try {
      await refreshService.refreshToken();

      if (!this.authTokens.getToken()?.access_token) {
        this._clearAuthSession();

        return { action: "redirect-to-login" };
      }

      return { action: "retry-request" };
    } catch (error) {
      this.log.debug("recoverAuthSession -> local auth recovery failed", error);
      this._clearAuthSession();

      return { action: "redirect-to-login" };
    }
  }

  private _clearAuthSession() {
    this.authTokens.clear();
    this.authContext.clear();
  }

  private _shouldTrySsoRecovery(response: Response) {
    const context = this.authContext.get();
    const serverAuthMethod = response.headers.get("Cayo-AuthMethod");

    if (!isValidAuthMode(context.authMode)) {
      return false;
    }

    if (!serverAuthMethod) {
      return true;
    }

    return isValidAuthMode(serverAuthMethod);
  }
}

const connectSso = () => {
  try {
    return getSSO();
  } catch {
    return undefined;
  }
};

const authRecoveryService = new AuthRecoveryService(tokenStorage, authContextStore, connectSso);

export default authRecoveryService;
